import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import MetricValueCardDouble, {
  type MetricItem,
} from "../../Common/MetricValueCardDouble";
import IconLoader from "@/features/Common/components/IconLoader";
import { getWellnessFlagIconProps } from "../../Common/wellnessFlagIconProps";
import { WellnessScoreStatus } from "../../Common/types";
import { getWellbeingStatus } from "./utils";

export interface MentalWellbeingInfoCardProps {
  depressionScore: number;
  anxietyScore: number;
  lastUpdated?: string;
  onCtaClick?: () => void;
}

const statusKey = (
  status: WellnessScoreStatus
): "healthy" | "needsAttention" | "needsSupport" => {
  switch (status) {
    case WellnessScoreStatus.Healthy:
      return "healthy";
    case WellnessScoreStatus.NeedsAttention:
      return "needsAttention";
    case WellnessScoreStatus.NeedsSupport:
      return "needsSupport";
    default:
      return "healthy";
  }
};

export default function MentalWellbeingInfoCard({
  depressionScore,
  anxietyScore,
  lastUpdated,
  onCtaClick,
}: MentalWellbeingInfoCardProps) {
  const { t } = useTranslation();
  const depStatus = getWellbeingStatus(depressionScore);
  const anxStatus = getWellbeingStatus(anxietyScore);

  const buildMetric = (
    labelKey: string,
    score: number,
    status: WellnessScoreStatus
  ): MetricItem => {
    const sk = statusKey(status);
    const statusLabelKey = `MentalWellbeing.InfoCard.statusLabel.${sk}.${labelKey}`;
    const messageKey = `MentalWellbeing.InfoCard.message.${sk}.${labelKey}`;
    return {
      label: t(`MentalWellbeing.InfoCard.${labelKey}` as any),
      value: String(score),
      unit: t("MentalWellbeing.InfoCard.unit"),
      status,
      statusLabel: t(statusLabelKey as any),
      children: (
        <Stack
          sx={{
            backgroundColor: "secondary.100",
            borderRadius: 2,
            p: 2,
          }}
          flexDirection="row"
          gap={1.5}
        >
          <IconLoader icon="FlagIcon" {...getWellnessFlagIconProps(status)} />
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.5,
              color: "neutral.700",
            }}
          >
            {t(messageKey as any)}
          </Typography>
        </Stack>
      ),
    };
  };

  const metrics: MetricItem[] = [
    buildMetric("depression", depressionScore, depStatus),
    buildMetric("anxiety", anxietyScore, anxStatus),
  ];

  return (
    <MetricValueCardDouble
      metricName={t("MentalWellbeing.InfoCard.metricName")}
      lastUpdated={lastUpdated}
      metrics={metrics}
      ctaText={t("MentalWellbeing.InfoCard.cta")}
      onCtaClick={onCtaClick}
    />
  );
}
